import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { FormField } from '@/components/FormField'
import { Toolbar } from '@/components/Toolbar'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/hooks/useToast'
import {
  useCreateEmployee,
  useEmployee,
  useUpdateEmployee,
  usePaySchedules,
  useEmployeeW4,
  useUpdateEmployeeW4,
  useEmployeeI9,
  useUpdateEmployeeI9,
} from '@/features/payroll/queries'
import { useAuth } from '@/auth/useAuth'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'
import type { Employee } from '@/features/payroll/types'
import type { CompensationInput, EmployeeCompensation } from '@/features/payroll/api'

const EMPLOYMENT_TYPES: Array<{ value: string; label: string }> = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'contract', label: 'Contract' },
  { value: 'fixed_term', label: 'Fixed-term' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'probationary', label: 'Probationary' },
  { value: 'apprentice', label: 'Apprentice' },
  { value: 'intern', label: 'Intern' },
  { value: 'trainee', label: 'Trainee' },
  { value: 'commission_only', label: 'Commission-only' },
  { value: 'union', label: 'Union' },
  { value: 'expat', label: 'Expat' },
]

const CLASSIFICATIONS: Array<{ value: string; label: string }> = [
  { value: 'w2_exempt', label: 'W-2 exempt' },
  { value: 'w2_non_exempt', label: 'W-2 non-exempt' },
  { value: 'w2_tipped', label: 'W-2 tipped' },
  { value: 'w2_piece_rate', label: 'W-2 piece-rate' },
  { value: 'w2_commission_only', label: 'W-2 commission-only' },
  { value: 'w2_union', label: 'W-2 union' },
  { value: 'w2_seasonal', label: 'W-2 seasonal' },
  { value: 'w2_intern', label: 'W-2 intern' },
  { value: 'w2_prevailing_wage', label: 'W-2 prevailing wage' },
  { value: 'w2_federal_contractor', label: 'W-2 federal contractor' },
  { value: 'corp_officer', label: 'Corporate officer' },
  { value: 'board_director', label: 'Board director' },
]

const FILING_STATUSES: Array<{ value: string; label: string }> = [
  { value: 'single', label: 'Single or married filing separately' },
  { value: 'married_filing_jointly', label: 'Married filing jointly' },
  { value: 'head_of_household', label: 'Head of household' },
]

const GENDERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Not specified' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'undisclosed', label: 'Prefer not to say' },
]

const ETHNICITIES: Array<{ value: string; label: string }> = [
  { value: '', label: 'Not specified' },
  { value: 'hispanic_latino', label: 'Hispanic or Latino' },
  { value: 'white', label: 'White' },
  { value: 'black_african_american', label: 'Black or African American' },
  { value: 'native_hawaiian_pacific_islander', label: 'Native Hawaiian or Pacific Islander' },
  { value: 'asian', label: 'Asian' },
  { value: 'american_indian_alaska_native', label: 'American Indian or Alaska Native' },
  { value: 'two_or_more', label: 'Two or more races' },
  { value: 'undisclosed', label: 'Prefer not to say' },
]

const EEO_JOB_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: '', label: 'Not specified' },
  { value: 'exec_senior_officials', label: 'Executive / senior-level officials and managers' },
  { value: 'first_mid_officials', label: 'First / mid-level officials and managers' },
  { value: 'professionals', label: 'Professionals' },
  { value: 'technicians', label: 'Technicians' },
  { value: 'sales_workers', label: 'Sales workers' },
  { value: 'admin_support', label: 'Administrative support workers' },
  { value: 'craft_workers', label: 'Craft workers' },
  { value: 'operatives', label: 'Operatives' },
  { value: 'laborers_helpers', label: 'Laborers and helpers' },
  { value: 'service_workers', label: 'Service workers' },
]

interface W4Form {
  filing_status: string
  multiple_jobs: boolean
  dependents_credit: string
  other_income: string
  deductions: string
  additional_withholding: string
  exempt: boolean
}

function blankW4(): W4Form {
  return {
    filing_status: 'single',
    multiple_jobs: false,
    dependents_credit: '',
    other_income: '',
    deductions: '',
    additional_withholding: '',
    exempt: false,
  }
}

interface I9Form {
  work_authorization_type: string
  work_authorization_expiry: string
  i9_section1_completed_at: string
  i9_section2_completed_at: string
  everify_case_number: string
  everify_status: string
}

function blankI9(): I9Form {
  return {
    work_authorization_type: '',
    work_authorization_expiry: '',
    i9_section1_completed_at: '',
    i9_section2_completed_at: '',
    everify_case_number: '',
    everify_status: 'not_submitted',
  }
}

const WORK_AUTH_TYPES = [
  { value: '', label: '-' },
  { value: 'citizen', label: 'U.S. citizen' },
  { value: 'noncitizen_national', label: 'Noncitizen national' },
  { value: 'permanent_resident', label: 'Lawful permanent resident' },
  { value: 'alien_authorized', label: 'Alien authorized to work' },
]

const EVERIFY_STATUSES = [
  { value: 'not_submitted', label: 'Not submitted' },
  { value: 'pending', label: 'Pending' },
  { value: 'employment_authorized', label: 'Employment authorized' },
  { value: 'tentative_nonconfirmation', label: 'Tentative nonconfirmation' },
  { value: 'final_nonconfirmation', label: 'Final nonconfirmation' },
  { value: 'closed', label: 'Closed' },
]

const I9_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  section1_complete: 'Section 1 complete',
  verified: 'Verified',
  reverification_due: 'Reverification due',
  expired: 'Expired',
}

interface FormState {
  first_name: string
  middle_name: string
  last_name: string
  email: string
  phone: string
  birth_date: string
  hire_date: string
  ssn_last4: string
  employment_type: string
  classification: string
  work_state: string
  gender: string
  ethnicity_race: string
  eeo_job_category: string
  address_line1: string
  city: string
  state_region: string
  postal_code: string
  pay_type: 'salaried' | 'hourly'
  base_salary: string
  hourly_rate: string
  standard_weekly_hours: string
  pay_schedule_id: string
}

function blank(): FormState {
  return {
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    birth_date: '',
    hire_date: new Date().toISOString().slice(0, 10),
    ssn_last4: '',
    employment_type: 'full_time',
    classification: 'w2_non_exempt',
    work_state: '',
    gender: '',
    ethnicity_race: '',
    eeo_job_category: '',
    address_line1: '',
    city: '',
    state_region: '',
    postal_code: '',
    pay_type: 'salaried',
    base_salary: '',
    hourly_rate: '',
    standard_weekly_hours: '40',
    pay_schedule_id: '',
  }
}

function centsToInput(cents: string | null | undefined): string {
  if (cents == null) return ''
  return (Number(cents) / 100).toString()
}

function compToForm(c: EmployeeCompensation): Partial<FormState> {
  const hourly = c.hourly_rate_minor_units != null && c.base_salary_minor_units == null
  return {
    pay_type: hourly ? 'hourly' : 'salaried',
    base_salary: centsToInput(c.base_salary_minor_units),
    hourly_rate: centsToInput(c.hourly_rate_minor_units),
    standard_weekly_hours: c.standard_weekly_hours != null ? String(c.standard_weekly_hours) : '40',
    pay_schedule_id: c.pay_schedule_id ?? '',
  }
}

function fromEmployee(e: Employee): FormState {
  return {
    first_name: e.first_name ?? '',
    middle_name: e.middle_name ?? '',
    last_name: e.last_name ?? '',
    email: e.email ?? '',
    phone: e.phone ?? '',
    birth_date: e.birth_date ?? '',
    hire_date: e.hire_date ?? '',
    ssn_last4: e.ssn_last4 ?? '',
    employment_type: e.employment_type ?? 'full_time',
    classification: e.classification ?? 'w2_non_exempt',
    work_state: e.work_state ?? '',
    gender: e.gender ?? '',
    ethnicity_race: e.ethnicity_race ?? '',
    eeo_job_category: e.eeo_job_category ?? '',
    address_line1: e.address_line1 ?? '',
    city: e.city ?? '',
    state_region: e.state_region ?? '',
    postal_code: e.postal_code ?? '',
    pay_type: 'salaried',
    base_salary: '',
    hourly_rate: '',
    standard_weekly_hours: '40',
    pay_schedule_id: '',
  }
}

export function EmployeeEditorPage() {
  const params = useParams<{ id?: string }>()
  const isEdit = !!params.id && params.id !== 'new'
  const employeeId = isEdit ? params.id : undefined
  const navigate = useNavigate()
  const { toast } = useToast()

  const existing = useEmployee(employeeId)
  const createMut = useCreateEmployee()
  const updateMut = useUpdateEmployee()
  const { data: schedules } = usePaySchedules()
  const regional = useRegionalDefaults()
  const { hasPermission } = useAuth()
  const canManageComp = hasPermission('payroll.compensation.manage')

  const w4Query = useEmployeeW4(employeeId)
  const w4Mut = useUpdateEmployeeW4()
  const i9Query = useEmployeeI9(employeeId)
  const i9Mut = useUpdateEmployeeI9()

  const [form, setForm] = useState<FormState>(blank)
  const [w4, setW4] = useState<W4Form>(blankW4)
  const [i9, setI9] = useState<I9Form>(blankI9)
  const [i9Status, setI9Status] = useState<string>('pending')
  const [i9Reverify, setI9Reverify] = useState<string | null>(null)

  useEffect(() => {
    if (isEdit && existing.data?.data) {
      setForm({
        ...fromEmployee(existing.data.data),
        ...(existing.data.compensation ? compToForm(existing.data.compensation) : {}),
      })
    }
  }, [isEdit, existing.data])

  useEffect(() => {
    const d = w4Query.data?.data
    if (d) {
      setW4({
        filing_status: d.filing_status || 'single',
        multiple_jobs: Boolean(d.multiple_jobs),
        dependents_credit: centsToInput(d.dependents_credit_minor_units),
        other_income: centsToInput(d.other_income_minor_units),
        deductions: centsToInput(d.deductions_minor_units),
        additional_withholding: centsToInput(d.additional_withholding_minor_units),
        exempt: Boolean(d.exempt),
      })
    }
  }, [w4Query.data])

  const updateW4 = <K extends keyof W4Form>(key: K, value: W4Form[K]) =>
    setW4((prev) => ({ ...prev, [key]: value }))

  useEffect(() => {
    const d = i9Query.data?.data
    if (d) {
      setI9({
        work_authorization_type: d.work_authorization_type || '',
        work_authorization_expiry: (d.work_authorization_expiry || '').slice(0, 10),
        i9_section1_completed_at: (d.i9_section1_completed_at || '').slice(0, 10),
        i9_section2_completed_at: (d.i9_section2_completed_at || '').slice(0, 10),
        everify_case_number: d.everify_case_number || '',
        everify_status: d.everify_status || 'not_submitted',
      })
      setI9Status(d.i9_status || 'pending')
      setI9Reverify(d.i9_reverification_due ? d.i9_reverification_due.slice(0, 10) : null)
    }
  }, [i9Query.data])

  const updateI9 = <K extends keyof I9Form>(key: K, value: I9Form[K]) =>
    setI9((prev) => ({ ...prev, [key]: value }))

  async function onSaveI9() {
    if (!employeeId) return
    try {
      const res = await i9Mut.mutateAsync({
        id: employeeId,
        body: {
          work_authorization_type: i9.work_authorization_type || null,
          work_authorization_expiry: i9.work_authorization_expiry || null,
          i9_section1_completed_at: i9.i9_section1_completed_at || null,
          i9_section2_completed_at: i9.i9_section2_completed_at || null,
          everify_case_number: i9.everify_case_number || null,
          everify_status: i9.everify_status,
        },
      })
      setI9Status(res.data.i9_status)
      setI9Reverify(res.data.i9_reverification_due ? res.data.i9_reverification_due.slice(0, 10) : null)
      toast({ title: 'I-9 saved', description: 'Work eligibility recorded.' })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed'
      toast({ title: 'Save failed', description: message, variant: 'destructive' })
    }
  }

  async function onSaveW4() {
    if (!employeeId) return
    try {
      await w4Mut.mutateAsync({
        id: employeeId,
        body: {
          filing_status: w4.filing_status,
          multiple_jobs: w4.multiple_jobs,
          dependents_credit_minor_units: Math.round(Number(w4.dependents_credit || 0) * 100),
          other_income_minor_units: Math.round(Number(w4.other_income || 0) * 100),
          deductions_minor_units: Math.round(Number(w4.deductions || 0) * 100),
          additional_withholding_minor_units: Math.round(Number(w4.additional_withholding || 0) * 100),
          exempt: w4.exempt,
        },
      })
      toast({ title: 'Withholding saved', description: 'Federal W-4 applied to future pay runs.' })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed'
      toast({ title: 'Save failed', description: message, variant: 'destructive' })
    }
  }

  function buildCompensation(): CompensationInput | undefined {
    if (form.pay_type === 'salaried') {
      if (!form.base_salary) return undefined
      return {
        pay_type: 'salaried',
        base_salary_minor_units: Math.round(Number(form.base_salary) * 100),
        base_salary_currency: regional.currency,
        standard_weekly_hours: Number(form.standard_weekly_hours) || 40,
        pay_schedule_id: form.pay_schedule_id || null,
      }
    }
    if (!form.hourly_rate) return undefined
    return {
      pay_type: 'hourly',
      hourly_rate_minor_units: Math.round(Number(form.hourly_rate) * 100),
      hourly_rate_currency: regional.currency,
      standard_weekly_hours: Number(form.standard_weekly_hours) || 40,
      pay_schedule_id: form.pay_schedule_id || null,
    }
  }

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const submitting = createMut.isPending || updateMut.isPending

  async function onSave() {
    const payload = {
      first_name: form.first_name,
      middle_name: form.middle_name,
      last_name: form.last_name,
      email: form.email,
      phone: form.phone,
      birth_date: form.birth_date,
      hire_date: form.hire_date,
      ssn_last4: form.ssn_last4.replace(/\D/g, '').slice(0, 4),
      employment_type: form.employment_type,
      classification: form.classification,
      work_state: form.work_state,
      gender: form.gender || null,
      ethnicity_race: form.ethnicity_race || null,
      eeo_job_category: form.eeo_job_category || null,
      address_line1: form.address_line1,
      city: form.city,
      state_region: form.state_region,
      postal_code: form.postal_code,
    }
    const compensation = buildCompensation()
    if (!payload.first_name || !payload.last_name || !payload.hire_date) {
      toast({
        title: 'Required fields missing',
        description: 'First name, last name, and hire date are required.',
        variant: 'destructive',
      })
      return
    }
    try {
      if (isEdit && employeeId && existing.data?.data) {
        await updateMut.mutateAsync({
          id: employeeId,
          body: { ...payload, compensation, expected_updated_at: existing.data.data.updated_at } as never,
        })
        toast({ title: 'Employee updated' })
      } else {
        const result = await createMut.mutateAsync({ ...payload, compensation } as never)
        toast({ title: 'Employee added', description: `${result.data.first_name} ${result.data.last_name}` })
        navigate(`/payroll/employees/${result.data.id}`)
        return
      }
      navigate('/payroll/employees')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed'
      toast({ title: 'Save failed', description: message, variant: 'destructive' })
    }
  }

  return (
    <Page
      title={isEdit ? 'Edit employee' : 'Add employee'}
      description={
        isEdit
          ? 'Update the employee record. Bank account changes and terminations require separate approvals.'
          : 'Capture the minimum information needed to add an employee. W-4, direct deposit, and benefits enrollment happen on the detail page.'
      }
      actions={
        <Toolbar>
          <Button variant="outline" onClick={() => navigate('/payroll/employees')}>
            <ArrowLeft className="mr-2 size-4" /> Back
          </Button>
          <Button onClick={onSave} disabled={submitting}>
            <Save className="mr-2 size-4" /> {submitting ? 'Saving…' : 'Save'}
          </Button>
        </Toolbar>
      }
    >
      <Card>
        <CardContent className="space-y-6 p-6">
          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Personal
            </h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                label="First name"
                required
                value={form.first_name}
                onChange={(e) => update('first_name', e.target.value)}
              />
              <FormField
                label="Middle name"
                value={form.middle_name}
                onChange={(e) => update('middle_name', e.target.value)}
              />
              <FormField
                label="Last name"
                required
                value={form.last_name}
                onChange={(e) => update('last_name', e.target.value)}
              />
              <FormField
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
              />
              <FormField
                label="Phone"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
              />
              <FormField
                label="Birth date"
                type="date"
                value={form.birth_date}
                onChange={(e) => update('birth_date', e.target.value)}
              />
              <FormField
                label="SSN (last 4 only)"
                inputMode="numeric"
                maxLength={4}
                hint="Full SSN is encrypted at rest via KMS. Only last 4 stored in plaintext for display."
                value={form.ssn_last4}
                onChange={(e) => update('ssn_last4', e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Employment
            </h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                label="Hire date"
                required
                type="date"
                value={form.hire_date}
                onChange={(e) => update('hire_date', e.target.value)}
              />
              <FormField
                label="Employment type"
                control={
                  <select
                    value={form.employment_type}
                    onChange={(e) => update('employment_type', e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {EMPLOYMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                }
              />
              <FormField
                label="Classification"
                control={
                  <select
                    value={form.classification}
                    onChange={(e) => update('classification', e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {CLASSIFICATIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                }
              />
              <FormField
                label="Work state (2-letter)"
                maxLength={2}
                placeholder="CA"
                value={form.work_state}
                onChange={(e) => update('work_state', e.target.value.toUpperCase().slice(0, 2))}
              />
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Voluntary self-identification (EEO)
            </h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Optional. Used only for aggregate EEO-1 reporting - never shown on payslips or shared with managers.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                label="Gender"
                control={
                  <select
                    value={form.gender}
                    onChange={(e) => update('gender', e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {GENDERS.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                }
              />
              <FormField
                label="Race / ethnicity"
                control={
                  <select
                    value={form.ethnicity_race}
                    onChange={(e) => update('ethnicity_race', e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {ETHNICITIES.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                }
              />
              <FormField
                label="EEO job category"
                control={
                  <select
                    value={form.eeo_job_category}
                    onChange={(e) => update('eeo_job_category', e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {EEO_JOB_CATEGORIES.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                }
              />
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Compensation
            </h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                label="Pay type"
                control={
                  <select
                    value={form.pay_type}
                    onChange={(e) => update('pay_type', e.target.value as FormState['pay_type'])}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="salaried">Salaried (annual)</option>
                    <option value="hourly">Hourly</option>
                  </select>
                }
              />
              {form.pay_type === 'salaried' ? (
                <FormField
                  label={`Annual salary (${regional.currency})`}
                  inputMode="decimal"
                  hint="Gross annual pay. Per-period gross is computed from the pay schedule frequency."
                  value={form.base_salary}
                  onChange={(e) => update('base_salary', e.target.value.replace(/[^\d.]/g, ''))}
                />
              ) : (
                <FormField
                  label={`Hourly rate (${regional.currency})`}
                  inputMode="decimal"
                  value={form.hourly_rate}
                  onChange={(e) => update('hourly_rate', e.target.value.replace(/[^\d.]/g, ''))}
                />
              )}
              <FormField
                label="Standard weekly hours"
                inputMode="decimal"
                hint="Used when no approved timesheet exists for the period."
                value={form.standard_weekly_hours}
                onChange={(e) => update('standard_weekly_hours', e.target.value.replace(/[^\d.]/g, ''))}
              />
              <FormField
                label="Pay schedule"
                control={
                  <select
                    value={form.pay_schedule_id}
                    onChange={(e) => update('pay_schedule_id', e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">Default (run schedule)</option>
                    {(schedules?.data ?? []).map((s) => (
                      <option key={s.id} value={s.id}>{s.name} - {s.frequency}</option>
                    ))}
                  </select>
                }
              />
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Home address
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="Address"
                value={form.address_line1}
                onChange={(e) => update('address_line1', e.target.value)}
              />
              <FormField
                label="City"
                value={form.city}
                onChange={(e) => update('city', e.target.value)}
              />
              <FormField
                label="State"
                maxLength={2}
                value={form.state_region}
                onChange={(e) => update('state_region', e.target.value.toUpperCase().slice(0, 2))}
              />
              <FormField
                label="Postal code"
                value={form.postal_code}
                onChange={(e) => update('postal_code', e.target.value)}
              />
            </div>
          </section>

          {isEdit && (
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Federal tax withholding (Form W-4)
                </h2>
                {canManageComp && (
                  <Button size="sm" variant="secondary" onClick={onSaveW4} disabled={w4Mut.isPending}>
                    <Save className="mr-2 size-4" /> {w4Mut.isPending ? 'Saving…' : 'Save W-4'}
                  </Button>
                )}
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Drives IRS Publication 15-T federal withholding on every pay run. Leave blank to default to single with no adjustments.
              </p>
              <fieldset disabled={!canManageComp} className="grid gap-4 sm:grid-cols-3">
                <FormField
                  label="Filing status (Step 1c)"
                  control={
                    <select
                      value={w4.filing_status}
                      onChange={(e) => updateW4('filing_status', e.target.value)}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      {FILING_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  }
                />
                <FormField
                  label={`Dependents credit (Step 3, ${regional.currency}/yr)`}
                  inputMode="decimal"
                  hint="$2,000 per qualifying child + $500 per other dependent."
                  value={w4.dependents_credit}
                  onChange={(e) => updateW4('dependents_credit', e.target.value.replace(/[^\d.]/g, ''))}
                />
                <FormField
                  label={`Other income (Step 4a, ${regional.currency}/yr)`}
                  inputMode="decimal"
                  value={w4.other_income}
                  onChange={(e) => updateW4('other_income', e.target.value.replace(/[^\d.]/g, ''))}
                />
                <FormField
                  label={`Deductions (Step 4b, ${regional.currency}/yr)`}
                  inputMode="decimal"
                  value={w4.deductions}
                  onChange={(e) => updateW4('deductions', e.target.value.replace(/[^\d.]/g, ''))}
                />
                <FormField
                  label={`Extra withholding (Step 4c, ${regional.currency}/period)`}
                  inputMode="decimal"
                  value={w4.additional_withholding}
                  onChange={(e) => updateW4('additional_withholding', e.target.value.replace(/[^\d.]/g, ''))}
                />
                <FormField
                  label="Multiple jobs (Step 2c)"
                  control={
                    <div className="flex h-9 items-center gap-2 text-sm">
                      <Checkbox
                        id="w4-multiple-jobs"
                        checked={w4.multiple_jobs}
                        onCheckedChange={(v) => updateW4('multiple_jobs', v === true)}
                      />
                      <label htmlFor="w4-multiple-jobs">Higher withholding rate</label>
                    </div>
                  }
                />
                <FormField
                  label="Exempt"
                  control={
                    <div className="flex h-9 items-center gap-2 text-sm">
                      <Checkbox
                        id="w4-exempt"
                        checked={w4.exempt}
                        onCheckedChange={(v) => updateW4('exempt', v === true)}
                      />
                      <label htmlFor="w4-exempt">No federal income tax withheld</label>
                    </div>
                  }
                />
              </fieldset>
            </section>
          )}

          {isEdit && (
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Work eligibility (Form I-9)
                </h2>
                {canManageComp && (
                  <Button size="sm" variant="secondary" onClick={onSaveI9} disabled={i9Mut.isPending}>
                    <Save className="mr-2 size-4" /> {i9Mut.isPending ? 'Saving…' : 'Save I-9'}
                  </Button>
                )}
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Status: <span className="font-medium text-foreground">{I9_STATUS_LABEL[i9Status] || i9Status}</span>
                {i9Reverify ? ` · reverification due ${i9Reverify}` : ''}
              </p>
              <fieldset disabled={!canManageComp} className="grid gap-4 sm:grid-cols-3">
                <FormField
                  label="Work authorization type"
                  control={
                    <select
                      value={i9.work_authorization_type}
                      onChange={(e) => updateI9('work_authorization_type', e.target.value)}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      {WORK_AUTH_TYPES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  }
                />
                <FormField
                  label="Work authorization expiry"
                  type="date"
                  hint="Leave blank for citizens / permanent work authorization."
                  value={i9.work_authorization_expiry}
                  onChange={(e) => updateI9('work_authorization_expiry', e.target.value)}
                />
                <div />
                <FormField
                  label="Section 1 completed"
                  type="date"
                  value={i9.i9_section1_completed_at}
                  onChange={(e) => updateI9('i9_section1_completed_at', e.target.value)}
                />
                <FormField
                  label="Section 2 completed"
                  type="date"
                  value={i9.i9_section2_completed_at}
                  onChange={(e) => updateI9('i9_section2_completed_at', e.target.value)}
                />
                <div />
                <FormField
                  label="E-Verify case number"
                  value={i9.everify_case_number}
                  onChange={(e) => updateI9('everify_case_number', e.target.value)}
                />
                <FormField
                  label="E-Verify status"
                  control={
                    <select
                      value={i9.everify_status}
                      onChange={(e) => updateI9('everify_status', e.target.value)}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      {EVERIFY_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  }
                />
              </fieldset>
            </section>
          )}
        </CardContent>
      </Card>
    </Page>
  )
}
